import { type WorkWithMe_userProfile$key } from '#app/__generated__/relay/WorkWithMe_userProfile.graphql.js';
import { WorkWithMeLoadedInquiryButton_userProfile$key } from '#app/__generated__/relay/WorkWithMeLoadedInquiryButton_userProfile.graphql.js';
import { type WorkWithMeMutation } from '#app/__generated__/relay/WorkWithMeMutation.graphql.js';
import { WorkWithMeQuery } from '#app/__generated__/relay/WorkWithMeQuery.graphql.js';
import { Checkbox } from '#app/components/Controls/src/Checkbox.js';
import {
  InquiryButton,
  InquiryButtonContainer,
} from '#app/components/Inquiry/Inquiry.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import {
  type ContraMutationOnCompleteCallback,
  useContraMutation,
} from '#app/hooks/useContraMutation.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { MailSendOutlineIcon } from '@contra/icons/icons';
import { graphql, useFragment, useLazyLoadQuery } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { captureException } from '@contra/sentry/react';
import { Suspense, useCallback } from 'react';
import { styled } from 'styled-components';

const CheckboxContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
`;

const LoadedInquiryButton = ({
  children,
  openInquiryOrOnboardingRedirectionModal,
  targetPersona,
  userProfileRef,
}: {
  readonly children?: React.ReactNode;
  readonly openInquiryOrOnboardingRedirectionModal: () => void;
  readonly targetPersona: 'Client' | 'Freelancer';
  readonly userProfileRef: WorkWithMeLoadedInquiryButton_userProfile$key;
}) => {
  const toast = useToast();
  const { track } = useAnalytics();

  const {
    analyticsId,
    generalInquiryCtaIsVisible,
    id: userProfileId,
    visitorCanEdit,
    visitorChatConversation,
  } = useFragment<WorkWithMeLoadedInquiryButton_userProfile$key>(
    graphql`
      fragment WorkWithMeLoadedInquiryButton_userProfile on UserProfile {
        id
        analyticsId
        generalInquiryCtaIsVisible
        visitorChatConversation {
          id
        }
        visitorCanEdit
      }
    `,
    userProfileRef,
  );

  const {
    visitor: { userAccount },
  } = useLazyLoadQuery<WorkWithMeQuery>(
    graphql`
      query WorkWithMeQuery {
        visitor {
          userAccount {
            createdJobPostings(
              first: 1
              filter: {
                status: [
                  APPROVED
                  ARCHIVED
                  PENDING
                  PENDING_REVISION
                  EXPIRED
                  PAUSED
                ]
              }
            ) {
              count
            }
          }
        }
      }
    `,
    {},
  );

  const currentUserHasOpenOpportunities =
    (userAccount?.createdJobPostings?.count ?? 0) > 0;

  const [updateWorkWithMe] = useContraMutation<WorkWithMeMutation>(graphql`
    mutation WorkWithMeMutation($input: UpdateUserProfileInput!) {
      updateUserProfile(input: $input) {
        errors {
          message
          __typename
        }
        userProfile {
          generalInquiryCtaIsVisible
        }
      }
    }
  `);

  const navigation = useNavigation();

  const onUpdateWorkWithMeComplete = useCallback<
    ContraMutationOnCompleteCallback<WorkWithMeMutation>
  >(
    (errors, response) => {
      const { apiErrors, hasErrors, payloadErrors } = errors;

      if (hasErrors) {
        if (payloadErrors) {
          toast.error('An error occurred updating. Please try again.');
        } else if (apiErrors) {
          toast.error(apiErrors[0].message);
        }
      } else {
        const { updateUserProfile } = response;
        const toastMessage = updateUserProfile.userProfile
          ?.generalInquiryCtaIsVisible
          ? `Inquiries are enabled! ${targetPersona}s can now get in touch with you about potential projects!`
          : 'Profile inquiries are now off';
        toast.success(toastMessage, { duration: 5_000 });
      }
    },
    [targetPersona, toast],
  );

  const onToggleWorkWithMe = useCallback(() => {
    const switchValue = !generalInquiryCtaIsVisible;

    track('switch', {
      action: 'tapped',
      switch_name: 'identity_work_with_me',
      switch_value: switchValue,
    });

    updateWorkWithMe({
      onCompleted: onUpdateWorkWithMeComplete,
      onError: (error) => {
        captureException(error);
        toast.error('An error occurred updating. Please try again.');
      },
      optimisticResponse: {
        updateUserProfile: {
          errors: null,
          userProfile: {
            generalInquiryCtaIsVisible: switchValue,
            id: userProfileId,
          },
        },
      },
      variables: {
        input: {
          generalInquiryCtaIsVisible: !generalInquiryCtaIsVisible,
        },
      },
    });
  }, [
    generalInquiryCtaIsVisible,
    onUpdateWorkWithMeComplete,
    toast,
    track,
    updateWorkWithMe,
    userProfileId,
  ]);

  const { openModal: openDisableWorkWithMeModal } = useModal({
    identifier: 'DisableWorkWithMeConfirmationModal',
    modalProps: {
      onToggleWorkWithMe,
      targetPersona,
    },
  });

  const onClickWorkWithMe = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'identity_work_with_me',
      has_open_opportunities: currentUserHasOpenOpportunities,
      owner_id: analyticsId,
    });

    if (visitorChatConversation?.id) {
      navigation.push(
        getRoute('/messages/@id', {
          args: {
            id: visitorChatConversation.id,
          },
        }),
      );
    } else {
      openInquiryOrOnboardingRedirectionModal();
    }
  }, [
    analyticsId,
    currentUserHasOpenOpportunities,
    navigation,
    openInquiryOrOnboardingRedirectionModal,
    track,
    visitorChatConversation,
  ]);

  return (
    <InquiryButton
      $displayAsDisabled={!generalInquiryCtaIsVisible}
      disabled={!visitorCanEdit && !generalInquiryCtaIsVisible}
      icon={MailSendOutlineIcon}
      onClick={
        visitorCanEdit
          ? generalInquiryCtaIsVisible
            ? () => openDisableWorkWithMeModal()
            : onToggleWorkWithMe
          : onClickWorkWithMe
      }
      removeMinWidth
      variant="V2Gradient"
    >
      {children}
    </InquiryButton>
  );
};

export const WorkWithMe = ({
  disableEditing = false,
  openInquiryOrOnboardingRedirectionModal,
  profileRef,
}: {
  readonly disableEditing?: boolean;
  readonly openInquiryOrOnboardingRedirectionModal: () => void;
  readonly profileRef: WorkWithMe_userProfile$key;
}) => {
  const { selectedUserType } = useSelectedUserType();

  const profile = useFragment<WorkWithMe_userProfile$key>(
    graphql`
      fragment WorkWithMe_userProfile on UserProfile {
        id
        analyticsId
        generalInquiryCtaIsVisible
        visitorCanEdit
        canReceiveInquiries
        visitorChatConversation {
          id
        }
        ...WorkWithMeLoadedInquiryButton_userProfile
      }
    `,
    profileRef,
  );

  const { canReceiveInquiries, generalInquiryCtaIsVisible, visitorCanEdit } =
    profile;

  if (
    canReceiveInquiries &&
    (visitorCanEdit ? selectedUserType !== 'CLIENT' : true)
  ) {
    const tooltipContentForDisabledInquiries = visitorCanEdit
      ? "Inquiries are off - Clients won't be able to contact you for projects"
      : 'This freelancer is not accepting inquiries at this time. Check back later!';

    const tooltipContentForEnabledInquiries = visitorCanEdit
      ? 'Inquiries are on - Clients can now contact you for projects!'
      : undefined;

    // in hiring view (CLIENT) -> toasts and tooltips should refer to `Independent`
    // in Independent view (CONTRACTOR) -> toasts and tooltips refer to `Client`
    const targetPersona =
      selectedUserType === 'CLIENT' ? 'Freelancer' : 'Client';

    const toggle =
      visitorCanEdit && !disableEditing ? (
        <CheckboxContainer>
          <Checkbox
            aria-label="Get In Touch"
            checked={generalInquiryCtaIsVisible}
            checkedColor="gray90"
            indeterminate
            isToggle
            onChange={() => {
              // no-op
              /**
               * Suppressing a console warning about a lack of onChange.
               * Since this is a toggle inside a button, we want the event
               * to bubble up to the button and not double-fire a mutation.
               */
            }}
            preChecked={generalInquiryCtaIsVisible}
          />
        </CheckboxContainer>
      ) : null;

    return (
      <InquiryButtonContainer
        tooltipContent={
          disableEditing
            ? undefined
            : generalInquiryCtaIsVisible
              ? tooltipContentForEnabledInquiries
              : tooltipContentForDisabledInquiries
        }
      >
        <Suspense
          fallback={
            <InquiryButton
              $displayAsDisabled={!generalInquiryCtaIsVisible}
              disabled={!visitorCanEdit && !generalInquiryCtaIsVisible}
              icon={MailSendOutlineIcon}
              removeMinWidth
              variant="V2Gradient"
            >
              Get In Touch
              {toggle}
            </InquiryButton>
          }
        >
          <LoadedInquiryButton
            openInquiryOrOnboardingRedirectionModal={
              openInquiryOrOnboardingRedirectionModal
            }
            targetPersona={targetPersona}
            userProfileRef={profile}
          >
            Get In Touch
            {toggle}
          </LoadedInquiryButton>
        </Suspense>
      </InquiryButtonContainer>
    );
  } else if (
    !canReceiveInquiries &&
    visitorCanEdit &&
    selectedUserType !== 'CLIENT'
  ) {
    return (
      <InquiryButtonContainer tooltipContent="Finish your profile to activate inquiries!">
        <InquiryButton
          disabled
          icon={MailSendOutlineIcon}
        >
          Get In Touch
        </InquiryButton>
      </InquiryButtonContainer>
    );
  } else {
    return null;
  }
};
